// 「わざと崩れて致命を誘い、弾いて知らない技をやり過ごす」手の対策 (配布版の既定 = fix・前の版は DEV ?execBait=off、片方だけは ?execBait=c|d)。
//
// 作者の原文 (10-31): 「他の通常時に分からない技は全部ガードをして、わざと自分の体幹を崩して、ボスの致命攻撃を誘発させて、
//   それを弾いて、自分がわからない技をやり過ごすっていう手法が通用してしまう」
// 作者の答え (10-31・答えるページ PSTrXfifAAwJL4jyA9hkxw): 「CDをメインにBも入れる。また、毎回続きからではなく、6割の確率で続きから。」・D は「ガードで崩れた時だけ」。
// 設計 = docs/research/2026-10-31-exec-bait-and-blade-finisher.md の C＋D (全ボス共通の土台)。B (一発で終わらない致命) はボスごと (剣舞は src/blade_finisher_v2.ts)。
//   C つづきから: 致命を弾かれたボスは、6割の回で、主人公を崩した連 (無ければ技) を次の待機で頭からやり直す。間合いの待ちも飛ばす。
//     → 知らない技は飛ばしきれない (崩れても同じ技がまた来ることが多い)。
//   D 命拾いだけ: ガードで崩れた時の致命弾きは、ボスの体幹が普段の弾き1回ぶんだけ溜まる (崩れる手前で止める)。
//     被弾・弾きで崩れた時は今のまま (普段の弾き3回ぶん・10-12 K-5「どんでん返し」)。
//   fix = C＋D (配布版)。
// URL を読むのは game.ts の DEV の箱の中。

import type { Boss, Player } from './types'

export type ExecBaitMode = 'off' | 'c' | 'd' | 'fix'

let mode: ExecBaitMode = 'fix'

/** 指定なし (null) は配布版の fix。 */
export function setExecBait(value: string | null | undefined): ExecBaitMode {
  mode = value === 'off' || value === 'c' || value === 'd' ? value : 'fix'
  return mode
}
export function execBaitMode(): ExecBaitMode { return mode }
export function stunEncoreOn(): boolean { return mode === 'c' || mode === 'fix' }
export function execBaitLifelineOn(): boolean { return mode === 'd' || mode === 'fix' }

/** D: ガードで崩れた時の致命弾きで溜まるボスの体幹。普段の弾き1回ぶん・崩れる手前 (最大-1) で止める・減らしはしない。 */
export function lifelineBossPosture(posture: number, maxPosture: number, oneParryGain: number): number {
  return Math.max(posture, Math.min(maxPosture - 1, posture + oneParryGain))
}

/** D を当てるか (崩れの原因がガード)。 */
export function lifelineApplies(p: Player): boolean {
  return execBaitLifelineOn() && p.stunCause === 'guard'
}

/** C: 主人公を崩した時に、崩した連・技を覚える (上書き)。 */
export function rememberStunSource(b: Boss): Boss {
  if (!stunEncoreOn() || !b.currentAttack || b.currentAttack.bossFinisher) return b
  return { ...b, stunEncore: { stringId: b.currentString?.id ?? null, attackId: b.currentAttack.id, armed: false } }
}

/** C: 致命を弾かれた時にやり直す割合 (10-31 作者「6割の確率で続きから」)。 */
export const EXEC_BAIT_ENCORE_ODDS = 0.6

/** C: 致命を弾かれた時に、6割でやり直しを待機へ予約する (外れたら覚えた技を捨てる＝いつもの抽選へ)。 */
export function armEncore(b: Boss, rand: () => number = Math.random): Boss {
  if (!stunEncoreOn() || !b.stunEncore) return b
  if (rand() >= EXEC_BAIT_ENCORE_ODDS) return { ...b, stunEncore: undefined }
  return { ...b, stunEncore: { ...b.stunEncore, armed: true } }
}

/** 予約済みのやり直しがあるか (待機の間合いの待ちを飛ばすのに使う)。 */
export function encoreArmed(b: Boss): boolean {
  return stunEncoreOn() && !!b.stunEncore?.armed
}

/** C: やり直しの間合いの待ち (ms)。弾かれの姿と戻りの後、すぐ構えに入る (反撃のすき無し)。 */
export const EXEC_BAIT_ENCORE_GAP_MS = 120
